import type { RenderContext, VideoFrame } from '../types';
import { WebGLShaderProgram, drawFullscreenQuad } from '../gpu/shader';
import { uploadCanvasToTexture } from '../gpu/texturePool';
import { BaseEffect } from './base';
import {
  VERTEX_FULLSCREEN,
  FRAG_BRIGHT_THRESHOLD,
  FRAG_BLUR_H,
  FRAG_BLUR_V,
  FRAG_BLOOM_COMPOSITE,
} from '../shaders/glsl';

const DEFAULTS = { intensity: 0.5, threshold: 0.7, radius: 8 };

let thresholdProg: WebGLShaderProgram | null = null;
let blurHProg: WebGLShaderProgram | null = null;
let blurVProg: WebGLShaderProgram | null = null;
let compositeProg: WebGLShaderProgram | null = null;

function prog(gl: WebGL2RenderingContext, which: 't' | 'h' | 'v' | 'c'): WebGLShaderProgram {
  if (which === 't') {
    if (!thresholdProg) thresholdProg = new WebGLShaderProgram(gl, VERTEX_FULLSCREEN, FRAG_BRIGHT_THRESHOLD);
    return thresholdProg;
  }
  if (which === 'h') {
    if (!blurHProg) blurHProg = new WebGLShaderProgram(gl, VERTEX_FULLSCREEN, FRAG_BLUR_H);
    return blurHProg;
  }
  if (which === 'v') {
    if (!blurVProg) blurVProg = new WebGLShaderProgram(gl, VERTEX_FULLSCREEN, FRAG_BLUR_V);
    return blurVProg;
  }
  if (!compositeProg) compositeProg = new WebGLShaderProgram(gl, VERTEX_FULLSCREEN, FRAG_BLOOM_COMPOSITE);
  return compositeProg;
}

export class GlowEffect extends BaseEffect {
  constructor(id: string, clip: Partial<import('../types').EffectTimelineClip>) {
    super(id, 'glow', clip, DEFAULTS);
  }

  protected renderGpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const gpu = context.gpu!;
    const gl = gpu.gl;
    const pool = gpu.texturePool;
    const w = frame.width;
    const h = frame.height;
    const base = pool.acquire(w, h);
    const bright = pool.acquire(w, h);
    const blurH = pool.acquire(w, h);
    const blurV = pool.acquire(w, h);
    uploadCanvasToTexture(gl, base, frame.canvas);

    const pass = (target: WebGLTexture, program: WebGLShaderProgram, src: WebGLTexture, setup: () => void) => {
      const fb = gl.createFramebuffer();
      gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, target, 0);
      gl.viewport(0, 0, w, h);
      program.use();
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, src);
      setup();
      drawFullscreenQuad(gl, program);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.deleteFramebuffer(fb);
    };

    pass(bright, prog(gl, 't'), base, () => {
      prog(gl, 't').setUniform1i('u_texture', 0);
      prog(gl, 't').setUniform1f('u_threshold', params.threshold);
    });
    pass(blurH, prog(gl, 'h'), bright, () => {
      prog(gl, 'h').setUniform1i('u_texture', 0);
      prog(gl, 'h').setUniform2f('u_texel', 1 / w, 1 / h);
      prog(gl, 'h').setUniform1f('u_radius', params.radius);
    });
    pass(blurV, prog(gl, 'v'), blurH, () => {
      prog(gl, 'v').setUniform1i('u_texture', 0);
      prog(gl, 'v').setUniform2f('u_texel', 1 / w, 1 / h);
      prog(gl, 'v').setUniform1f('u_radius', params.radius);
    });

    const out = pool.acquire(w, h);
    const fb = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, out, 0);
    gl.viewport(0, 0, w, h);
    const cp = prog(gl, 'c');
    cp.use();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, base);
    cp.setUniform1i('u_base', 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, blurV);
    cp.setUniform1i('u_bloom', 1);
    cp.setUniform1f('u_intensity', params.intensity);
    drawFullscreenQuad(gl, cp);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.deleteFramebuffer(fb);

    const result = this.readTextureToFrame(gl, out, w, h);
    [base, bright, blurH, blurV, out].forEach((t) => pool.release(t));
    return result;
  }

  private readTextureToFrame(gl: WebGL2RenderingContext, tex: WebGLTexture, w: number, h: number): VideoFrame {
    const fb = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    const pixels = new Uint8Array(w * h * 4);
    gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.deleteFramebuffer(fb);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const img = ctx.createImageData(w, h);
      for (let y = 0; y < h; y += 1) {
        const srcRow = (h - 1 - y) * w * 4;
        img.data.set(pixels.subarray(srcRow, srcRow + w * 4), y * w * 4);
      }
      ctx.putImageData(img, 0, 0);
    }
    return { width: w, height: h, canvas, texture: null };
  }

  protected renderCpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    // Simplified CPU bloom: blur bright areas via canvas filter
    const canvas = document.createElement('canvas');
    canvas.width = frame.width;
    canvas.height = frame.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return frame;
    ctx.drawImage(frame.canvas as CanvasImageSource, 0, 0);
    ctx.globalCompositeOperation = 'lighter';
    ctx.filter = `blur(${params.radius}px) brightness(1.2)`;
    ctx.globalAlpha = params.intensity;
    ctx.drawImage(frame.canvas as CanvasImageSource, 0, 0);
    ctx.filter = 'none';
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    ctx.drawImage(frame.canvas as CanvasImageSource, 0, 0);
    return { width: frame.width, height: frame.height, canvas, texture: null };
  }
}
